import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const ReportNavigationSidebar = ({ 
  sections = [],
  activeSection,
  onSectionClick,
  isOpen = false,
  onToggle,
  className = ''
}) => {
  const [expandedSections, setExpandedSections] = useState(new Set());
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef?.current && !sidebarRef?.current?.contains(event?.target) && isOpen) {
        const toggleButton = document.querySelector('[data-report-nav-toggle]');
        if (toggleButton && !toggleButton?.contains(event?.target)) {
          onToggle?.(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(sectionId)) {
      newExpanded?.delete(sectionId);
    } else {
      newExpanded?.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const handleSectionClick = (section) => {
    onSectionClick?.(section);
    if (window.innerWidth < 1024) {
      onToggle?.(false);
    }
  };

  const renderSection = (section, level = 0) => {
    const hasChildren = section?.children && section?.children?.length > 0;
    const isExpanded = expandedSections?.has(section?.id);
    const isActive = activeSection === section?.id;

    return (
      <div key={section?.id} className="mb-1">
        <button
          onClick={() => {
            if (hasChildren) {
              toggleSection(section?.id);
            }
            handleSectionClick(section);
          }}
          className={`
            w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm
            transition-all duration-250 focus-ring text-left
            ${isActive 
              ? 'bg-primary text-primary-foreground font-medium' 
              : 'text-foreground hover:bg-muted'
            }
          `}
          style={{ paddingLeft: `${12 + level * 16}px` }}
        >
          {hasChildren && (
            <Icon 
              name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
              size={14}
              className="flex-shrink-0"
            />
          )}
          {!hasChildren && <div className="w-3.5" />}
          
          <span className="flex-1 truncate">{section?.title}</span>
          
          {section?.badge && (
            <span className="flex-shrink-0 px-1.5 py-0.5 text-xs rounded caption bg-muted text-muted-foreground">
              {section?.badge}
            </span>
          )}
        </button>
        {hasChildren && isExpanded && (
          <div className="mt-1">
            {section?.children?.map(child => renderSection(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const defaultSections = sections?.length > 0 ? sections : [
    {
      id: 'executive-summary',
      title: 'Resumen Ejecutivo',
      badge: '1'
    },
    {
      id: 'project-description',
      title: 'Descripción del Proyecto',
      badge: '2',
      children: [
        { id: 'project-overview', title: 'Visión General' },
        { id: 'project-location', title: 'Ubicación Geográfica' },
        { id: 'project-timeline', title: 'Cronograma' }
      ]
    },
    {
      id: 'methodology',
      title: 'Metodología',
      badge: '3',
      children: [
        { id: 'baseline-scenario', title: 'Escenario de Línea Base' },
        { id: 'project-scenario', title: 'Escenario del Proyecto' },
        { id: 'additionality', title: 'Adicionalidad' }
      ]
    },
    {
      id: 'mrv-plan',
      title: 'Plan MRV',
      badge: '4',
      children: [
        { id: 'monitoring', title: 'Monitoreo' },
        { id: 'reporting', title: 'Reporte' },
        { id: 'verification', title: 'Verificación' }
      ]
    },
    {
      id: 'carbon-accounting',
      title: 'Contabilidad de Carbono',
      badge: '5'
    },
    {
      id: 'safeguards',
      title: 'Salvaguardas',
      badge: '6',
      children: [
        { id: 'environmental', title: 'Ambientales' },
        { id: 'social', title: 'Sociales' }
      ]
    },
    {
      id: 'annexes',
      title: 'Anexos',
      badge: '7'
    }
  ];

  return (
    <>
      <button
        data-report-nav-toggle
        onClick={() => onToggle?.(!isOpen)}
        className="lg:hidden fixed bottom-6 left-6 z-800 flex items-center justify-center w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-all duration-250 focus-ring"
        aria-label="Toggle report navigation"
      >
        <Icon name={isOpen ? 'X' : 'List'} size={24} />
      </button>
      <aside
        ref={sidebarRef}
        className={`
          fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] w-72 bg-card border-r border-border
          transition-transform duration-250 z-800 overflow-hidden flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${className}
        `}
      >
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={20} color="var(--color-primary)" />
            <h2 className="font-semibold text-lg font-mono">Navegación</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <nav className="space-y-1">
            {defaultSections?.map(section => renderSection(section))}
          </nav>
        </div>

        <div className="p-4 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Download"
            iconPosition="left"
            onClick={() => console.log('Export report')}
          >
            Exportar Informe
          </Button>
        </div>
      </aside>
    </>
  );
};

export default ReportNavigationSidebar;